import { withAITracking } from '@microsoft/applicationinsights-react-js';
import Sync from '@mui/icons-material/Sync';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { Suspense, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useProductCenterApi } from 'api';
import { unitStructureQuery } from 'api/queries';
import { AreaName } from 'domain/constants';
import { isSuperUser } from 'domain/user/User';
import { SectionButton } from 'library/components/controls/buttons/SectionButton';
import { SectionTitleButtons } from 'library/components/controls/layout/SectionTitleButtons';
import { OperationType } from 'library/models/enums/OperationType';
import { selectUser } from 'library/redux/application';
import { useAppSelector } from 'library/redux/hooks';
import {
	calculateDrivelineTotal,
	sortStructure,
} from 'library/utils/helpers/Unit';
import AuthorizeComponent from 'main/auth/AuthorizeComponent';
import { useCheckConsoleDebuggingReadAccess } from 'main/auth/checkUserAccess';
import { reactPlugin } from 'main/azureInsights/config';
import { useResolvedUnitIds } from 'modules/Shared/Components/Hooks/useNumberParam';
import { InstallationHeaderView } from 'modules/Shared/Components/Unit/Configuration/InstallationHeaderView';
import { UnitDrivelineItemView } from 'modules/Shared/Components/Unit/UnitDrivelineItemView';
import { isDetailedSegmentYacht } from 'modules/Shared/State/unit/Functions';
import EditDeleteButton from 'modules/UnitEdit/Steps/Shared/Components/EditDeleteButton';
import { ChangedByInfoDisplay } from 'modules/UnitOverview/Components/ChangedByInfoDisplay';
import { UnitEditConfigurationButton } from 'modules/UnitOverview/Components/UnitEditConfigurationButton';

const ConfigurationSectionPage = (): React.JSX.Element => {
	const user = useAppSelector(selectUser);
	const { t } = useTranslation(['unitCreation', 'unitOverview']);
	const unitsIds = useResolvedUnitIds();
	const { enqueueSnackbar } = useSnackbar();
	const { checkConsoleDebuggingReadAccess } =
		useCheckConsoleDebuggingReadAccess();

	const { api } = useProductCenterApi();

	const syncUnitMutation = useMutation<void, unknown, number>({
		mutationFn: async (data) => {
			await api.forceUnitSync(data.toString());
		},
		onSuccess: () => {
			enqueueSnackbar(t(`unitOverview:force-sync-unit-success`), {
				variant: 'success',
			});
		},
	});

	const { data } = useSuspenseQuery(unitStructureQuery(unitsIds.id));

	const structure = sortStructure(data);

	const drivelinesTotal = useMemo(
		() => calculateDrivelineTotal(structure?.installations || []),
		[data.id]
	);
	const isAnyYacht = useMemo(
		() =>
			structure?.installations.some((installation) =>
				isDetailedSegmentYacht(installation.detailedSegment)
			) ?? false,
		[structure]
	);
	const isSU = useMemo(() => isSuperUser(user), [user]);

	const syncUnit = useCallback(async () => {
		try {
			await syncUnitMutation.mutateAsync(unitsIds.id);
		} catch (error) {
			checkConsoleDebuggingReadAccess() && console.error(error);
			enqueueSnackbar(t(`unitOverview:force-sync-unit-error`), {
				variant: 'error',
			});
		}
	}, [unitsIds.id]);

	return (
		<>
			<SectionTitleButtons>
				<AuthorizeComponent
					areaName={AreaName.ForceSync}
					permission={OperationType.Read}>
					<SectionButton
						text={t('unitOverview:force-sync-unit')}
						icon={<Sync />}
						onClick={syncUnit}
					/>
				</AuthorizeComponent>
				<AuthorizeComponent
					areaName={AreaName.Unit}
					permission={OperationType.Write}>
					{/* a temp solution is to add a key to Suspense to work with RRD v7*/}

					<Suspense
						fallback={<Skeleton width={200} height={40} />}
						key="unit-edit-configuration-button">
						<UnitEditConfigurationButton
							isReadonly={
								structure?.isBlueprint || (isAnyYacht && !isSU)
							}
							unitId={unitsIds.id}
							unitPublicId={unitsIds.publicId}
						/>
					</Suspense>
				</AuthorizeComponent>
				<AuthorizeComponent
					areaName={AreaName.UnitDelete}
					permission={OperationType.Delete}
					key={`delete-unit-${unitsIds.id}`}>
					<EditDeleteButton unitId={unitsIds.id} />
				</AuthorizeComponent>
			</SectionTitleButtons>
			{structure?.installations.map((installation) => (
				<Box
					key={`installation-${installation.id}`}
					sx={{
						mt: 2,
					}}>
					<InstallationHeaderView
						mainSegment={data.mainSegment ?? null}
						installation={installation}
					/>
					{installation.drivelines.map((driveline) => (
						<UnitDrivelineItemView
							disabled
							unitId={unitsIds.id ?? 0}
							key={`driveline-unit-${driveline.id}`}
							installation={installation}
							driveline={driveline}
							total={drivelinesTotal}
							isBlueprint={data.isBlueprint}
						/>
					))}
				</Box>
			))}
			<Box
				sx={{
					display: 'flex',
					justifyContent: 'flex-end',
					marginTop: 3,
				}}>
				<ChangedByInfoDisplay
					changedByInfo={data.configurationChangedBy}
				/>
			</Box>
		</>
	);
};

export default withAITracking(
	reactPlugin,
	ConfigurationSectionPage,
	'UnitOverviewConfigurationSectionPage',
	'application-insight-container'
);
